// One Piece jigsaw — pick a character and a size, solve it against the clock.
//
// It plays like a real table: the pieces start scattered, you drag them
// anywhere, and two neighbours lock together the moment you bring them close
// in the right position. A locked group then moves as one. It is solved when
// every piece is in one group; the picture glides to the middle and shows whole.
//
// The piece shapes follow Dillon's "Jigsaw puzzle" pen on CodePen
// (https://codepen.io/Dillo/pen/QWKLYab, MIT licence): corners nudged off the
// grid, and each inner side bent into the classic knob.
//
// All geometry lives in image space (0..1). A group is drawn by clipping the
// union of its pieces once, so joined pieces show no seam, and its outline,
// bevel and shadow are baked into bitmaps that are only redone when the group
// changes. Dragging just repaints those bitmaps, which keeps 60 pieces smooth
// on a phone.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import useGameFullscreen from '../hooks/useGameFullscreen';
import './JigsawPuzzle.css';
import GuestNudge from '../components/GuestNudge/GuestNudge';
import RaceRules from '../components/RaceRules/RaceRules';
import { trackEvent } from '../services/analyticsService';
import { deviceToken, getSavedName, saveName, loadChallenge, submitChallengeScore } from '../services/challengeService';
import { useUsername } from '../hooks/useUsername';
import Avatar from '../components/Avatar/Avatar';

const LEVELS = [
  // spread: how much table each loose piece gets, as a multiple of its size
  { pieces: 12, cols: 3, rows: 4, spread: 1.7 },
  { pieces: 30, cols: 5, rows: 6, spread: 1.8 },
  { pieces: 64, cols: 8, rows: 8, spread: 1.55 },
];

export const CHARACTERS = [
  ['monkey-d-luffy', 'Luffy'], ['roronoa-zoro', 'Zoro'], ['nami', 'Nami'],
  ['vinsmoke-sanji', 'Sanji'], ['nico-robin', 'Robin'], ['brook', 'Brook'],
  ['shanks', 'Shanks'], ['trafalgar-law', 'Law'], ['portgas-d-ace', 'Ace'],
  ['boa-hancock', 'Hancock'], ['dracule-mihawk', 'Mihawk'], ['yamato', 'Yamato'],
  ['charlotte-katakuri', 'Katakuri'], ['buggy', 'Buggy'], ['crocodile', 'Crocodile'],
  ['gol-d-roger', 'Roger'], ['silvers-rayleigh', 'Rayleigh'], ['monkey-d-garp', 'Garp'],
  ['smoker', 'Smoker'], ['blackbeard', 'Blackbeard'], ['arlong', 'Arlong'],
  ['perona', 'Perona'], ['nefertari-vivi', 'Vivi'], ['big-mom', 'Big Mom'],
];
const NAME = Object.fromEntries(CHARACTERS);
// Pictures are portrait, 3 wide by 4 tall, so the whole figure shows: hat to trousers.
const ASPECT = 4 / 3;
const imageOf = (slug) => `/puzzle/${slug}.jpg`;
const thumbOf = (slug) => `/puzzle/thumb/${slug}.jpg`;

const BEST_KEY = 'opq-puzzle-best';
const readBest = () => {
  try { return JSON.parse(localStorage.getItem(BEST_KEY)) || {}; } catch (e) { return {}; }
};
const writeBest = (all) => {
  try { localStorage.setItem(BEST_KEY, JSON.stringify(all)); } catch (e) { /* private mode */ }
};
// The round in progress, so a refresh or a slip of the finger brings it back.
const SESSION_KEY = 'opq-puzzle-session';
const readSession = () => {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY)); } catch (e) { return null; }
};
const clearSession = () => {
  try { localStorage.removeItem(SESSION_KEY); } catch (e) { /* private mode */ }
};

// Every solved puzzle, newest first, kept on this device.
const HISTORY_KEY = 'opq-puzzle-history';
const readHistory = () => {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY)) || []; } catch (e) { return []; }
};
const addHistory = (entry) => {
  const list = [entry, ...readHistory()].slice(0, 40);
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(list)); } catch (e) { /* private mode */ }
  return list;
};
const whenLabel = (t) => {
  const d = new Date(t); const now = new Date();
  const days = Math.floor((new Date(now.toDateString()) - new Date(d.toDateString())) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const formatTime = (ms) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

// ---- geometry (image space, 0..1) ----------------------------------------

const lerp = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
// The cut can be seeded, so everyone in a race gets exactly the same pieces.
let random = Math.random;
const rnd = (a, b) => a + (b - a) * random();
const mulberry32 = (seed) => () => {
  let t = (seed += 0x6d2b79f5);
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

// Bend a straight side p0->p1 into a knob that points towards the side ca-cb.
// Ported from Dillon's twist0.
function knob(p0, p1, ca, cb) {
  const dxh = p1.x - p0.x; const dyh = p1.y - p0.y;
  const mid0 = lerp(p0, p1, 0.5); const mid1 = lerp(ca, cb, 0.5);
  const dxv = mid1.x - mid0.x; const dyv = mid1.y - mid0.y;
  const sx = rnd(0.8, 1); const sy = rnd(0.9, 1); const mid = rnd(0.45, 0.55);
  const at = (h, v) => ({ x: p0.x + h * dxh + v * dxv, y: p0.y + h * dyh + v * dyv });
  const pa = at(mid - (1 / 12) * sx, (1 / 12) * sy);
  const pb = at(mid - (2 / 12) * sx, (3 / 12) * sy);
  const pc = at(mid, (4 / 12) * sy);
  const pd = at(mid + (2 / 12) * sx, (3 / 12) * sy);
  const pe = at(mid + (1 / 12) * sx, (1 / 12) * sy);
  const off = (p, fx, fy) => ({ x: p.x + fx, y: p.y + fy });
  return [p0,
    off(p0, (5 / 12) * dxh * 0.52, (5 / 12) * dyh * 0.52),
    off(pa, -(1 / 12) * dxv * 0.72, -(1 / 12) * dyv * 0.72), pa,
    off(pa, (1 / 12) * dxv * 0.72, (1 / 12) * dyv * 0.72),
    off(pb, -(1 / 12) * dxv * 0.92, -(1 / 12) * dyv * 0.92), pb,
    off(pb, (1 / 12) * dxv * 0.52, (1 / 12) * dyv * 0.52),
    off(pc, -(2 / 12) * dxh * 0.40, -(2 / 12) * dyh * 0.40), pc,
    off(pc, (2 / 12) * dxh * 0.40, (2 / 12) * dyh * 0.40),
    off(pd, (1 / 12) * dxv * 0.52, (1 / 12) * dyv * 0.52), pd,
    off(pd, -(1 / 12) * dxv * 0.92, -(1 / 12) * dyv * 0.92),
    off(pe, (1 / 12) * dxv * 0.72, (1 / 12) * dyv * 0.72), pe,
    off(pe, -(1 / 12) * dxv * 0.72, -(1 / 12) * dyv * 0.72),
    off(p1, -(5 / 12) * dxh * 0.52, -(5 / 12) * dyh * 0.52), p1];
}

function makeShapes(cols, rows, seed) {
  random = seed ? mulberry32(seed) : Math.random;
  const J = 0.12;
  const corner = [];
  for (let r = 0; r <= rows; r++) {
    corner[r] = [];
    for (let c = 0; c <= cols; c++) {
      let x = (c + rnd(-J, J)) / cols;
      let y = (r + rnd(-J, J)) / rows;
      if (c === 0) x = 0; if (c === cols) x = 1;
      if (r === 0) y = 0; if (r === rows) y = 1;
      corner[r][c] = { x, y };
    }
  }
  // h[r][c]: along horizontal line r, corner (r,c) -> (r,c+1).
  // v[r][c]: along vertical line c, corner (r,c) -> (r+1,c).
  const h = []; const v = [];
  for (let r = 0; r <= rows; r++) {
    h[r] = [];
    for (let c = 0; c < cols; c++) {
      const a = corner[r][c]; const b = corner[r][c + 1];
      if (r === 0 || r === rows) h[r][c] = [a, b];
      else if (random() < 0.5) h[r][c] = knob(a, b, corner[r - 1][c], corner[r - 1][c + 1]);
      else h[r][c] = knob(a, b, corner[r + 1][c], corner[r + 1][c + 1]);
    }
  }
  for (let r = 0; r < rows; r++) {
    v[r] = [];
    for (let c = 0; c <= cols; c++) {
      const a = corner[r][c]; const b = corner[r + 1][c];
      if (c === 0 || c === cols) v[r][c] = [a, b];
      else if (random() < 0.5) v[r][c] = knob(a, b, corner[r][c - 1], corner[r + 1][c - 1]);
      else v[r][c] = knob(a, b, corner[r][c + 1], corner[r + 1][c + 1]);
    }
  }
  random = Math.random;
  return { h, v };
}

// Sides of piece (r,c) in clockwise order, each as a list of points.
function sidesOf(shapes, r, c) {
  return [
    shapes.h[r][c],
    shapes.v[r][c + 1],
    [...shapes.h[r + 1][c]].reverse(),
    [...shapes.v[r][c]].reverse(),
  ];
}

function traceSide(path, pts, SW, SH, ox, oy, first) {
  if (first) path.moveTo(pts[0].x * SW + ox, pts[0].y * SH + oy);
  if (pts.length === 2) { path.lineTo(pts[1].x * SW + ox, pts[1].y * SH + oy); return; }
  for (let i = 1; i < pts.length - 1; i += 3) {
    path.bezierCurveTo(
      pts[i].x * SW + ox, pts[i].y * SH + oy,
      pts[i + 1].x * SW + ox, pts[i + 1].y * SH + oy,
      pts[i + 2].x * SW + ox, pts[i + 2].y * SH + oy,
    );
  }
}

function piecePath(shapes, r, c, SW, SH, ox = 0, oy = 0) {
  const p = new Path2D();
  sidesOf(shapes, r, c).forEach((pts, i) => traceSide(p, pts, SW, SH, ox, oy, i === 0));
  p.closePath();
  return p;
}

const NEIGHBOUR = [[-1, 0], [0, 1], [1, 0], [0, -1]]; // top right bottom left

export default function JigsawPuzzle() {
  const [slug, setSlug] = useState(CHARACTERS[0][0]);
  const [levelIdx, setLevelIdx] = useState(0);
  const [phase, setPhase] = useState('pick'); // pick | loading | play | done
  const [elapsed, setElapsed] = useState(0);
  const [joined, setJoined] = useState(0);
  const [peek, setPeek] = useState(false);
  const [best, setBest] = useState(readBest);
  const [history, setHistory] = useState(readHistory);
  const [result, setResult] = useState(null);
  const [ready, setReady] = useState(true);
  const [confirmBox, setConfirmBox] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const leavingRef = useRef(false);
  const inGame = !!(location.state && location.state.jpPlay);

  // ---- racing friends in a room ----------------------------------------
  // roomForRace: the room this puzzle is raced in (from ?room= or "Race friends").
  // race: the match being played { room, match, seed, name }.
  const { username } = useUsername();
  const [roomForRace, setRoomForRace] = useState(() => new URLSearchParams(location.search).get('room'));
  const [race, setRaceState] = useState(null);
  const raceRef = useRef(null);
  const setRace = (r) => { raceRef.current = r; setRaceState(r); };
  const [racePlayers, setRacePlayers] = useState([]);
  const PENDING_FINISH = 'opq-race-pending';
  useEffect(() => {
    const resend = () => {
      let p = null;
      try { p = JSON.parse(localStorage.getItem(PENDING_FINISH)); } catch (e) { p = null; }
      if (!p) return;
      submitChallengeScore(p).then(() => { try { localStorage.removeItem(PENDING_FINISH); } catch (e) { /* fine */ } })
        .catch((err) => { if (err && !/connection|fetch|network|load failed/i.test(err.message)) { try { localStorage.removeItem(PENDING_FINISH); } catch (e2) { /* fine */ } } });
    };
    resend();
    window.addEventListener('online', resend);
    return () => window.removeEventListener('online', resend);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Replies to progress reports can arrive out of order (fast joins, a weak
  // phone signal); only the newest one may change the list.
  const raceSeq = useRef(0);
  const roundAt = useRef(Date.now()); // when this round began (Start race / Race again / Restart)
  const raceShown = useRef(0);
  const lanesLeft = () => {
    const me = raceRef.current ? raceRef.current.name.toLowerCase() : '';
    return racePlayers.filter((p) => p.name.toLowerCase() !== me && p.cur && p.cur.status !== 'left'
      && p.cur.score < (game.current ? game.current.pieces.length - 1 : 99)
      && new Date(p.cur.at).getTime() > roundAt.current - 120e3).length;
  };
  const [joinNote, setJoinNote] = useState('');
  const knownRacers = useRef(null);
  const showRace = (seq, players) => {
    if (seq < raceShown.current) return;
    raceShown.current = seq;
    const list = players || [];
    // Someone new in the race: say so for a few seconds.
    const names = list.map((p) => p.name);
    if (knownRacers.current) {
      const fresh = names.filter((n) => !knownRacers.current.includes(n) && (!raceRef.current || n.toLowerCase() !== raceRef.current.name.toLowerCase()));
      if (fresh.length) { setJoinNote(fresh.length === 1 ? `${fresh[0]} joined` : `${fresh.length} players joined`); setTimeout(() => setJoinNote(''), 4000); }
    }
    knownRacers.current = names;
    setRacePlayers(list);
  };
  const [raceName, setRaceName] = useState(getSavedName);
  const [raceMsg, setRaceMsg] = useState('');
  const [raceCopied, setRaceCopied] = useState(false);
  const [raceBusy, setRaceBusy] = useState(false);
  // Solo or race. Race never makes a room by itself: it goes back to this
  // device's last race room, and a new one is only made on "New room".
  const [mode, setMode] = useState(() => (new URLSearchParams(location.search).get('room') ? 'race' : 'solo'));
  const [editName, setEditName] = useState(false);
  const LAST_ROOM = 'opq-race-room';
  useEffect(() => { if (roomForRace) { try { localStorage.setItem(LAST_ROOM, roomForRace); } catch (e) { /* fine */ } } }, [roomForRace]);
  const goRace = () => {
    setMode('race'); setRaceMsg('');
    if (!roomForRace) { let last = null; try { last = localStorage.getItem(LAST_ROOM); } catch (e) { /* fine */ } if (last) setRoomForRace(last); }
  };
  const goSolo = () => { setMode('solo'); setRoomForRace(null); setRace(null); setRaceMsg(''); };
  const [roomMembers, setRoomMembers] = useState([]);
  useEffect(() => { if (!raceName && username) setRaceName(username); }, [username, raceName]);

  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const game = useRef(null);
  const scratch = useRef(null);

  const level = LEVELS[levelIdx];

  // ---- baking ------------------------------------------------------------
  const bakeGroup = useCallback((grp) => {
    const g = game.current;
    const { S, SH, dpr, cols, rows, shapes } = g;
    const cw = S / cols; const ch = SH / rows;
    const M = Math.max(cw, ch) * 0.55 + 8; // room for knobs and shadow
    const x0 = grp.minC * cw - M; const y0 = grp.minR * ch - M;
    const w = (grp.maxC - grp.minC + 1) * cw + 2 * M;
    const hgt = (grp.maxR - grp.minR + 1) * ch + 2 * M;
    const inGroup = new Set(grp.pieces.map((p) => p.id));

    const union = new Path2D();
    const outline = new Path2D();
    grp.pieces.forEach((p) => {
      union.addPath(piecePath(shapes, p.r, p.c, S, SH, -x0, -y0));
      sidesOf(shapes, p.r, p.c).forEach((pts, i) => {
        const nr = p.r + NEIGHBOUR[i][0]; const nc = p.c + NEIGHBOUR[i][1];
        const outer = nr < 0 || nc < 0 || nr >= rows || nc >= cols || !inGroup.has(nr * cols + nc);
        if (outer) traceSide(outline, pts, S, SH, -x0, -y0, true);
      });
    });

    const mk = () => {
      const cv = document.createElement('canvas');
      cv.width = Math.ceil(w * dpr); cv.height = Math.ceil(hgt * dpr);
      const cx = cv.getContext('2d');
      cx.scale(dpr, dpr);
      return [cv, cx];
    };

    // the piece itself, with a soft bevel on its outer edge only
    const [bmp, bx] = mk();
    bx.save();
    bx.clip(union);
    bx.drawImage(g.img, -x0, -y0, S, SH);
    const e = Math.min(3, Math.max(1.5, cw / 40));
    bx.lineWidth = e * 1.6;
    bx.translate(e / 2, -e / 2);
    bx.strokeStyle = 'rgba(0,0,0,0.35)';
    bx.stroke(outline);
    bx.translate(-e, e);
    bx.strokeStyle = 'rgba(255,255,255,0.4)';
    bx.stroke(outline);
    bx.restore();

    // shadow and gold glow, drawn by casting from far off-canvas
    const cast = (color, blur, dx, dy) => {
      const [cv, cx] = mk();
      cx.shadowColor = color;
      cx.shadowBlur = blur * dpr;
      cx.shadowOffsetX = (10000 + dx) * dpr;
      cx.shadowOffsetY = dy * dpr;
      cx.translate(-10000, 0);
      cx.fillStyle = '#000';
      cx.fill(union);
      return cv;
    };
    grp.bmp = bmp;
    grp.shadow = cast('rgba(0,0,0,0.5)', 5, -4, 4);
    grp.glow = cast('rgba(255,200,40,0.95)', 10, 0, 0);
    grp.lime = cast('rgba(120,255,90,0.95)', 12, 0, 0);
    grp.bx0 = x0; grp.by0 = y0; grp.bw = w; grp.bh = hgt;
  }, []);

  const bakeAll = useCallback(() => {
    const g = game.current;
    g.paths = g.pieces.map((p) => piecePath(g.shapes, p.r, p.c, g.S, g.SH));
    g.groups.forEach(bakeGroup);
  }, [bakeGroup]);

  // ---- drawing -----------------------------------------------------------
  const draw = useCallback(() => {
    const g = game.current;
    const canvas = canvasRef.current;
    if (!g || !canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(g.dpr, 0, 0, g.dpr, 0, 0);
    ctx.clearRect(0, 0, g.W, g.H);

    if (g.won) {
      // The finished picture always fits ABOVE the result card, shrinking if
      // the board is short (race track, small phone), so the two never overlap.
      const card = document.querySelector('.jp-done-card');
      const room = g.H - (card ? card.getBoundingClientRect().height + 28 : 230) - 8;
      const k = Math.min(1, Math.max(0.3, room / g.SH));
      const w = g.S * k; const h = g.SH * k;
      const x = (g.W - w) / 2; const y = Math.max(8, Math.min(g.groups[0].y, room + 8 - h));
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.45)'; ctx.shadowBlur = 14; ctx.shadowOffsetX = -5; ctx.shadowOffsetY = 6;
      ctx.drawImage(g.img, x, y, w, h);
      ctx.restore();
      return;
    }
    if (g.peek) {
      ctx.globalAlpha = 0.9;
      const ph = Math.min(g.H * 0.9, g.W * 0.9 * ASPECT); const pw = ph / ASPECT;
      ctx.drawImage(g.img, (g.W - pw) / 2, (g.H - ph) / 2, pw, ph);
      ctx.globalAlpha = 1;
      return;
    }
    const now = performance.now();
    // The camera: zoom and offset set by the + / − / show-all buttons.
    const v = g.view || { z: 1, ox: 0, oy: 0 };
    ctx.setTransform(g.dpr * v.z, 0, 0, g.dpr * v.z, g.dpr * v.ox, g.dpr * v.oy);
    g.groups.forEach((grp) => {
      const lifted = g.drag && g.drag.grp === grp;
      const flash = grp.flashUntil && grp.flashUntil > now;
      const halo = flash ? grp.lime : lifted ? grp.glow : grp.shadow;
      const x = grp.x + grp.bx0; const y = grp.y + grp.by0;
      if (grp.rot) {
        ctx.save();
        const cx = x + grp.bw / 2; const cy = y + grp.bh / 2;
        ctx.translate(cx, cy); ctx.rotate(grp.rot); ctx.translate(-cx, -cy);
      }
      ctx.drawImage(halo, x, y, grp.bw, grp.bh);
      if (lifted || flash) ctx.drawImage(halo, x, y, grp.bw, grp.bh);
      ctx.drawImage(grp.bmp, x, y, grp.bw, grp.bh);
      if (grp.rot) ctx.restore();
    });
  }, []);

  // Fit the canvas to the room left on screen, under the header and above the
  // phone's bottom bar.
  const measure = useCallback(() => {
    const wrap = wrapRef.current;
    const top = wrap.getBoundingClientRect().top;
    const footer = document.querySelector('.mobile-footer');
    let bottom = 8;
    if (footer && getComputedStyle(footer).display !== 'none') {
      const fTop = footer.getBoundingClientRect().top;
      if (fTop < window.innerHeight) bottom = window.innerHeight - fTop + 6;
    }
    // leave room for the zoom row under the board
    const row = document.querySelector('.jp-zoom-row');
    const rowH = row ? Math.ceil(row.getBoundingClientRect().height) + 6 : 0;
    return { W: wrap.clientWidth, H: Math.max(300, Math.floor(window.innerHeight - top - bottom - rowH)) };
  }, []);

  // Size of the finished picture: as big as fits while the loose pieces still
  // get a cell each around it.
  const chooseSize = (W, H, cols, rows, n, spread) => {
    let S = Math.min(W * 0.72, (H * 0.8) / ASPECT);
    const grid = (size) => ({
      gx: Math.max(1, Math.floor(W / ((size / cols) * spread))),
      gy: Math.max(1, Math.floor(H / (((size * ASPECT) / rows) * spread))),
    });
    for (let i = 0; i < 60; i++) {
      const { gx, gy } = grid(S);
      if (gx * gy >= n) return { S, SH: S * ASPECT, gx, gy };
      S *= 0.96;
    }
    return { S, SH: S * ASPECT, ...grid(S) };
  };

  const sizeCanvas = (W, H, dpr) => {
    const canvas = canvasRef.current;
    canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
    canvas.style.width = `${W}px`; canvas.style.height = `${H}px`;
  };

  const layout = useCallback((first) => {
    const g = game.current;
    if (!g || !wrapRef.current || !canvasRef.current) return;
    const { W, H } = measure();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    sizeCanvas(W, H, dpr);
    g.view = { z: 1, ox: 0, oy: 0 };
    const fit = chooseSize(W, H, g.cols, g.rows, g.pieces.length, g.spread);

    if (first && g.restore) {
      Object.assign(g, { W, H, dpr, S: fit.S, SH: fit.SH });
      g.groups.forEach((grp, i) => {
        grp.x = W / 2 + g.restore[i].ox * fit.S;
        grp.y = H / 2 + g.restore[i].oy * fit.SH;
      });
      delete g.restore;
    } else if (first) {
      Object.assign(g, { W, H, dpr, S: fit.S, SH: fit.SH });
      const cellW = W / fit.gx; const cellH = H / fit.gy;
      const cells = [];
      for (let y = 0; y < fit.gy; y++) for (let x = 0; x < fit.gx; x++) cells.push({ cx: (x + 0.5) * cellW, cy: (y + 0.5) * cellH });
      cells.sort(() => Math.random() - 0.5);
      const cw = fit.S / g.cols; const ch = fit.SH / g.rows;
      const homeX = (W - fit.S) / 2; const homeY = (H - fit.SH) / 2;
      g.groups.forEach((grp, i) => {
        const p = grp.pieces[0];
        const spot = cells[i % cells.length];
        const jx = rnd(-0.42, 0.42) * Math.max(0, cellW - cw);
        const jy = rnd(-0.42, 0.42) * Math.max(0, cellH - ch);
        grp.sx = spot.cx + jx - (p.c + 0.5) * cw;
        grp.sy = spot.cy + jy - (p.r + 0.5) * ch;
        grp.spin = rnd(-1, 1) * Math.PI * 1.2;
        if (g.state === 'ready') { grp.x = homeX; grp.y = homeY; } else { grp.x = grp.sx; grp.y = grp.sy; }
      });
    } else {
      const k = fit.S / g.S;
      const oldW = g.W; const oldH = g.H;
      g.groups.forEach((grp) => {
        // keep each group's centre at the same place relative to the screen
        const cx = grp.x + ((grp.minC + grp.maxC + 1) / 2) * (g.S / g.cols);
        const cy = grp.y + ((grp.minR + grp.maxR + 1) / 2) * (g.SH / g.rows);
        const ncx = (cx / oldW) * W; const ncy = (cy / oldH) * H;
        grp.x = ncx - (cx - grp.x) * k;
        grp.y = ncy - (cy - grp.y) * k;
      });
      Object.assign(g, { W, H, dpr, S: fit.S, SH: fit.SH });
      if (g.won) { g.groups[0].x = (W - g.S) / 2; g.groups[0].y = Math.max(8, Math.min((H - g.SH) / 2, H - g.SH - 210)); }
    }
    bakeAll();
    draw();
  }, [bakeAll, draw, measure]);

  // ---- starting a round --------------------------------------------------
  const start = useCallback((chosen, idx, saved, seed) => {
    const lv = LEVELS[idx];
    // a restored round began before the refresh: count back by its clock
    roundAt.current = saved ? Date.now() - (saved.elapsed || 0) - 60e3 : Date.now();
    if (!saved) clearSession();
    trackEvent(saved ? 'puzzle_resume' : 'puzzle_start', { character: chosen, pieces: lv.pieces });
    setPhase('loading');
    setJoined(saved ? saved.joined : 0); setElapsed(saved ? saved.elapsed : 0);
    setResult(null); setPeek(false); setReady(!saved || saved.state === 'ready');
    const img = new Image();
    img.onload = () => {
      const { cols, rows } = lv;
      const pieces = [];
      for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) pieces.push({ id: r * cols + c, r, c });
      const makeGroup = (members) => {
        const grp = { pieces: members, x: 0, y: 0 };
        grp.minR = Math.min(...members.map((p) => p.r)); grp.maxR = Math.max(...members.map((p) => p.r));
        grp.minC = Math.min(...members.map((p) => p.c)); grp.maxC = Math.max(...members.map((p) => p.c));
        members.forEach((p) => { p.group = grp; });
        return grp;
      };
      const restoring = saved && saved.state === 'playing';
      const groups = restoring
        ? saved.groups.map((sg) => makeGroup(sg.ids.map((id) => pieces[id])))
        : pieces.map((p) => makeGroup([p])).sort(() => Math.random() - 0.5);
      game.current = {
        img, cols, rows, pieces, groups, shapes: saved ? saved.shapes : makeShapes(cols, rows, seed),
        drag: null, startedAt: restoring ? Date.now() - saved.elapsed : null, won: false, peek: false,
        slug: chosen, idx, count: lv.pieces, spread: lv.spread, state: restoring ? 'playing' : 'ready',
        restore: restoring ? saved.groups : null,
      };
      if (process.env.NODE_ENV !== 'production') window.__jigsaw = game.current;
      // A step in the history for the round, so the phone's Back and the
      // header arrow come back to the picker instead of leaving the site.
      if (!(window.history.state && window.history.state.usr && window.history.state.usr.jpPlay)) {
        navigate('/puzzle', { state: { jpPlay: true } });
      }
      setPhase('play');
      window.scrollTo(0, 0);
    };
    img.onerror = () => setPhase('pick');
    img.src = imageOf(chosen);
  }, [layout, navigate]);

  // Lay the table out once the canvas for a new round is on the page.
  useEffect(() => {
    if (phase === 'play' && game.current && !game.current.S) layout(true);
  }, [phase, layout]);

  // The sticky Start row sits just above the phone's bottom bar, measured, so
  // it can never slide under it.
  useEffect(() => {
    const setFooter = () => {
      const f = document.querySelector('.mobile-footer');
      const h = f && getComputedStyle(f).display !== 'none' ? Math.ceil(f.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--jp-footer', `${h}px`);
    };
    setFooter();
    window.addEventListener('resize', setFooter);
    return () => window.removeEventListener('resize', setFooter);
  }, [phase]);

  // On a phone the game gets the whole screen while a round is open.
  useEffect(() => {
    const on = phase === 'play' || phase === 'done';
    if (on && game.current && game.current.S) setTimeout(() => layout(game.current.state === 'ready'), 60);
  }, [phase, layout]);
  useGameFullscreen(phase === 'play' || phase === 'done');

  // ---- clock -------------------------------------------------------------
  useEffect(() => {
    if (phase !== 'play') return undefined;
    const id = setInterval(() => {
      const g = game.current;
      if (g && g.startedAt && !g.won) setElapsed(Date.now() - g.startedAt);
    }, 250);
    return () => clearInterval(id);
  }, [phase]);

  // ---- resize / rotate ---------------------------------------------------
  useEffect(() => {
    if (phase !== 'play' && phase !== 'done') return undefined;
    let t;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => layout(!!(game.current && game.current.state === 'ready')), 150);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [phase, layout]);

  const saveSession = useCallback(() => {
    const g = game.current;
    if (!g || g.won || !g.S || (g.state !== 'playing' && g.state !== 'ready')) return;
    const data = {
      slug: g.slug, idx: g.idx, state: g.state, shapes: g.shapes, race: raceRef.current,
      elapsed: g.startedAt ? Date.now() - g.startedAt : 0,
      joined: g.pieces.length - g.groups.length,
      groups: g.groups.map((grp) => ({
        ids: grp.pieces.map((p) => p.id),
        ox: (grp.x - g.W / 2) / g.S,
        oy: (grp.y - g.H / 2) / g.SH,
      })),
    };
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(data)); } catch (e) { /* storage full or blocked */ }
  }, []);

  // Coming back to the page picks up the round that was left open.
  useEffect(() => {
    const saved = readSession();
    const params = new URLSearchParams(window.location.search);
    const pick = /^(.+)-(\d+)$/.exec(params.get('p') || '');
    const urlRoom = params.get('room');
    if (saved && LEVELS[saved.idx] && NAME[saved.slug] && (!urlRoom || (saved.race && saved.race.room === urlRoom))) {
      setSlug(saved.slug);
      setLevelIdx(saved.idx);
      if (saved.race) { setRace(saved.race); setRoomForRace(saved.race.room); }
      start(saved.slug, saved.idx, saved);
    } else if (pick && NAME[pick[1]]) {
      // From a room's puzzle: same character and size, ready to start.
      setSlug(pick[1]);
      setLevelIdx(Math.max(0, LEVELS.findIndex((l) => l.pieces === Number(pick[2]))));
    }
    // only on first visit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A refresh or closing the tab asks first while a round is running, and the
  // round is saved either way.
  useEffect(() => {
    if (phase !== 'play') return undefined;
    const onLeave = (e) => {
      saveSession();
      const g = game.current;
      if (g && g.state === 'playing' && !g.won) { e.preventDefault(); e.returnValue = ''; }
    };
    const onHide = () => saveSession();
    window.addEventListener('beforeunload', onLeave);
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('beforeunload', onLeave);
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [phase, saveSession]);

  // ---- joining -----------------------------------------------------------
  const tryMerge = (g, moved) => {
    const cw = g.S / g.cols; const ch = g.SH / g.rows;
    const snap = Math.max(12, Math.min(cw, ch) * 0.28);
    let target = moved;
    let mergedAny = false;
    let again = true;
    while (again) {
      again = false;
      for (const other of g.groups) {
        if (other === target) continue;
        if (Math.hypot(other.x - target.x, other.y - target.y) > snap) continue;
        const touching = target.pieces.some((p) => other.pieces.some((q) => Math.abs(p.r - q.r) + Math.abs(p.c - q.c) === 1));
        if (!touching) continue;
        // the bigger group stays put; the smaller one joins it
        const [keep, gone] = other.pieces.length >= target.pieces.length ? [other, target] : [target, other];
        gone.pieces.forEach((p) => { p.group = keep; keep.pieces.push(p); });
        keep.minR = Math.min(keep.minR, gone.minR); keep.maxR = Math.max(keep.maxR, gone.maxR);
        keep.minC = Math.min(keep.minC, gone.minC); keep.maxC = Math.max(keep.maxC, gone.maxC);
        g.groups.splice(g.groups.indexOf(gone), 1);
        target = keep;
        mergedAny = true;
        again = true;
        break;
      }
    }
    if (mergedAny) {
      bakeGroup(target);
      g.groups.splice(g.groups.indexOf(target), 1);
      g.groups.push(target);
      target.flashUntil = performance.now() + 450;
      const tick = () => { draw(); if (target.flashUntil > performance.now()) requestAnimationFrame(tick); else draw(); };
      requestAnimationFrame(tick);
      if (navigator.vibrate) { try { navigator.vibrate(15); } catch (err) { /* ignore */ } }
    }
    return mergedAny;
  };

  // Each join tells the room how far along this player is; the reply carries
  // everyone else's progress back. One small request per join, no polling.
  const reportRace = (g, final) => {
    const r = raceRef.current;
    if (!r || !g.startedAt) return;
    const seq = ++raceSeq.current;
    const report = {
      code: r.match, name: r.name, score: g.pieces.length - g.groups.length,
      time: Math.floor((Date.now() - g.startedAt) / 1000), final,
    };
    // A finish must never be lost to a dropped signal: it waits on the phone
    // and is sent again when the connection is back (see the effect below).
    if (final) { try { localStorage.setItem(PENDING_FINISH, JSON.stringify(report)); } catch (e) { /* fine */ } }
    submitChallengeScore(report).then(async (d) => {
      if (final) { try { localStorage.removeItem(PENDING_FINISH); } catch (e) { /* fine */ } }
      showRace(seq, d.players);
      if (final) {
        // The finish must show: read the table again once everything has landed.
        const fresh = await loadChallenge(r.match);
        if (fresh) { const s2 = ++raceSeq.current; showRace(s2, fresh.players); d = fresh; }
        const done = (d.players || []).filter((p) => p.score >= g.pieces.length - 1);
        const place = done.findIndex((p) => p.name.toLowerCase() === r.name.toLowerCase()) + 1;
        trackEvent('puzzle_race_finish', { room: r.room, place, finished: done.length, seconds: Math.floor((Date.now() - g.startedAt) / 1000) });
      }
    }).catch(() => {});
  };

  const startRace = async () => {
    const name = raceName.trim();
    if (name.length < 2) { setRaceMsg('Type your name for the race first.'); return; }
    setRaceMsg('');
    setPhase('loading');
    try {
      const res = await fetch('/api/room', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'play', code: roomForRace, game: `puzzle-${slug}-${LEVELS[levelIdx].pieces}`, name, token: deviceToken() }),
      });
      const json = await res.json();
      if (!json.success) {
        setRaceMsg(json.taken ? 'That name is taken in this room. Pick another.' : (json.error || 'Could not start the race.'));
        setPhase('pick');
        return;
      }
      saveName(name);
      setRace({ room: roomForRace, match: json.code, seed: json.seed, name });
      setRacePlayers([]);
      // On the board at once as 0 joined, so friends see who is in the race.
      await submitChallengeScore({ code: json.code, name, score: 0, time: 0, final: false }).catch(() => {});
      trackEvent('puzzle_race_start', { room: roomForRace, character: slug, pieces: LEVELS[levelIdx].pieces });
      start(slug, levelIdx, null, json.seed);
    } catch (e) {
      setRaceMsg('No connection. Try again.');
      setPhase('pick');
    }
  };

  // "Race friends": a room with this puzzle in it, and its link to send.
  const makeRaceRoom = async () => {
    if (raceBusy) return;
    // The room's owner is its maker's name: no name, no owner (rara's room).
    if (raceName.trim().length < 2) { setRaceMsg('Type your name first.'); return; }
    setRaceMsg('');
    setRaceBusy(true);
    try {
      const res = await fetch('/api/room', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create', name: `${NAME[slug]} puzzle race`, by: raceName.trim(), token: deviceToken() }),
      });
      const json = await res.json();
      if (!json.success) { setRaceMsg('Could not make the room. Try again.'); return; }
      // Put this puzzle in the room now, so a friend who opens the link before
      // the race starts sees the same character and size, not a blank list.
      const name = raceName.trim();
      await fetch('/api/room', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'play', code: json.code, game: `puzzle-${slug}-${LEVELS[levelIdx].pieces}`, name, token: deviceToken() }),
      }).catch(() => {});
      setRoomForRace(json.code);
      trackEvent('puzzle_race_room', { room: json.code });
    } catch (e) { setRaceMsg('No connection. Try again.'); } finally { setRaceBusy(false); }
  };

  // Who is already in the room, shown on the race box.
  useEffect(() => {
    if (!roomForRace) { setRoomMembers([]); return undefined; }
    let alive = true;
    fetch(`/api/room?code=${encodeURIComponent(roomForRace)}`).then((r) => r.json())
      .then((d) => {
        if (!alive || !d || !d.success) return;
        setRoomMembers((d.members || []).map((m) => m.name));
        // Opening a room picks the puzzle its friends are racing (the newest one),
        // so nobody starts a different character or size by accident.
        const latest = (d.games || []).filter((x) => x.puzzle).pop();
        if (latest && NAME[latest.puzzle.slug] && !raceRef.current) {
          setSlug(latest.puzzle.slug);
          setLevelIdx(Math.max(0, LEVELS.findIndex((l) => l.pieces === latest.puzzle.pieces)));
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [roomForRace]);

  const raceLink = roomForRace ? `${window.location.origin}/room/${roomForRace}` : '';
  // The phone's own share sheet (WhatsApp, Discord…) where there is one,
  // otherwise the link is copied.
  const shareRace = async () => {
    // The link sits inside the message: some apps keep only the text (Chrome
    // on a Mac sent it to Google search without the link). Computers copy.
    const text = `Race me on this One Piece puzzle: ${NAME[slug]}, ${LEVELS[levelIdx].pieces} pieces. Fastest time wins.\n${raceLink}`;
    trackEvent('puzzle_race_share', { room: roomForRace });
    const phone = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    if (phone && navigator.share) {
      try { await navigator.share({ text }); return; } catch (e) { if (e && e.name === 'AbortError') return; }
    }
    try { await navigator.clipboard.writeText(raceLink); setRaceCopied(true); setTimeout(() => setRaceCopied(false), 3000); } catch (e) { window.prompt('Copy this link', raceLink); }
  };

  // Where everyone is: on joining the race (or coming back after a refresh),
  // and again whenever the tab comes back into view.
  useEffect(() => {
    if (!race) return undefined;
    const load = () => { const seq = ++raceSeq.current; return loadChallenge(race.match).then((d) => d && showRace(seq, d.players)); };
    const fresh = () => { if (document.visibilityState === 'visible') load(); };
    load();
    document.addEventListener('visibilitychange', fresh);
    return () => document.removeEventListener('visibilitychange', fresh);
  }, [race]);

  const finish = (g) => {
    g.won = true;
    reportRace(g, true);
    clearSession();
    const ms = Date.now() - g.startedAt;
    setElapsed(ms);
    const all = readBest();
    const key = `${g.slug}|${g.count}`;
    const record = !all[key] || ms < all[key];
    if (record) { all[key] = ms; writeBest(all); }
    setBest(all);
    trackEvent('puzzle_solve', { character: g.slug, pieces: g.count, seconds: Math.round(ms / 1000), new_best: record });
    setHistory(addHistory({ slug: g.slug, pieces: g.count, ms, at: Date.now(), record }));
    // glide the finished picture to the middle
    const grp = g.groups[0];
    const fx = grp.x; const fy = grp.y;
    const tx = (g.W - g.S) / 2;
    const ty = Math.max(8, Math.min((g.H - g.SH) / 2, g.H - g.SH - 210));
    const t0 = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - t0) / 650);
      const e = 1 - (1 - t) ** 3;
      grp.x = fx + (tx - fx) * e; grp.y = fy + (ty - fy) * e;
      draw();
      if (t < 1) requestAnimationFrame(step);
      else settle();
    };
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      grp.x = tx; grp.y = ty;
      draw();
      setTimeout(() => { setResult({ ms, record }); setPhase('done'); setTimeout(() => draw(), 80); }, 900);
    };
    requestAnimationFrame(step);
    setTimeout(settle, 900);
  };

  // The picture breaks apart: every piece flies to its own spot with a spin,
  // different each round. The clock starts when they land.
  const breakApart = () => {
    const g = game.current;
    if (!g || g.state !== 'ready') return;
    g.state = 'scatter';
    setReady(false);
    const from = g.groups.map((grp) => [grp.x, grp.y]);
    const t0 = performance.now();
    const D = 900;
    const step = (now) => {
      const t = Math.min(1, (now - t0) / D);
      g.groups.forEach((grp, i) => {
        const d = Math.min(1, Math.max(0, (t * D - (i % 12) * 18) / (D - 216)));
        const e = 1 - (1 - d) ** 3;
        grp.x = from[i][0] + (grp.sx - from[i][0]) * e;
        grp.y = from[i][1] + (grp.sy - from[i][1]) * e;
        grp.rot = grp.spin * (1 - e) * Math.sin(e * Math.PI);
      });
      draw();
      if (t < 1) { requestAnimationFrame(step); return; }
      land();
    };
    // Land on time even if the browser holds back animation frames.
    const land = () => {
      if (g.state !== 'scatter') return;
      g.groups.forEach((grp) => { grp.rot = 0; grp.x = grp.sx; grp.y = grp.sy; });
      g.state = 'playing';
      g.startedAt = Date.now();
      draw();
      saveSession();
    };
    requestAnimationFrame(step);
    setTimeout(land, D + 200);
  };

  // ---- live race: everyone's progress every 4 s while the round is on ----
  // (Fahad, 2026-10-21: a friend's lane must move even when you are not
  // joining anything.) Only while racing and the tab is visible; it stops
  // 3 minutes after you finish, or after 20 minutes.
  useEffect(() => {
    if (!race || phase !== 'play' || ready) return undefined;
    const began = Date.now();
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - began > 20 * 60e3) { clearInterval(id); return; }
      const seq = ++raceSeq.current;
      loadChallenge(race.match).then((d) => d && showRace(seq, d.players));
    }, 2500);
    return () => clearInterval(id);
  }, [race, phase, ready]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!race || !result) return undefined;
    let n = 0;
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible' || ++n > 45) { if (n > 45) clearInterval(id); return; }
      const seq = ++raceSeq.current;
      loadChallenge(race.match).then((d) => d && showRace(seq, d.players));
    }, 2500);
    return () => clearInterval(id);
  }, [race, result]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- "Start together": one shared moment for everyone waiting ----------
  // A racer presses it; the server sets a start 10 s ahead. Waiting phones
  // ask every 3 s (only while on the ready screen, at most 5 minutes) and
  // count down by the server's clock, so all boards break apart together.
  const [goAt, setGoAt] = useState(null);
  const [goLeft, setGoLeft] = useState(0);
  const [lateMsg, setLateMsg] = useState('');
  const takeStart = (d) => {
    if (!d || !d.room || !d.room.startAt || !d.serverNow) return;
    const left = new Date(d.room.startAt) - new Date(d.serverNow);
    if (left > -1500 && left < 30000) { setGoAt(Date.now() + left); return; }
    // The others started together less than 3 minutes ago: you are late, so
    // you go now instead of waiting on a picture nobody else is looking at.
    // A late joiner did not play in that start; someone pressing Race again
    // after finishing it did, and must not be thrown straight back in.
    const meRow = raceRef.current && (d.players || []).find((p) => p.name.toLowerCase() === raceRef.current.name.toLowerCase());
    const playedIt = meRow && meRow.cur && meRow.cur.status && meRow.cur.status !== 'ready'
      && new Date(meRow.cur.at) > new Date(d.room.startAt);
    if (!playedIt && left <= -1500 && left > -180e3 && game.current && game.current.state === 'ready') {
      setLateMsg(`The others started ${Math.round(-left / 1000)} s ago. Go!`);
      setTimeout(() => setLateMsg(''), 6000);
      breakApart();
    }
  };
  useEffect(() => {
    if (!race || !ready || phase !== 'play' || goAt) return undefined;
    let asks = 0;
    const ask = () => {
      if (document.visibilityState !== 'visible' || ++asks > 100) return;
      loadChallenge(race.match).then(takeStart);
    };
    ask();
    const id = setInterval(ask, 2500);
    return () => clearInterval(id);
  }, [race, ready, phase, goAt]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!goAt) return undefined;
    const id = setInterval(() => {
      const left = goAt - Date.now();
      setGoLeft(left);
      if (left <= 0) {
        clearInterval(id);
        setGoAt(null);
        if (game.current && game.current.state === 'ready') breakApart();
      }
    }, 150);
    return () => clearInterval(id);
  }, [goAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const [goBusy, setGoBusy] = useState(false);
  const [goMsg, setGoMsg] = useState('');
  const startTogetherNow = async () => {
    if (!race || goBusy) return;
    setGoBusy(true); setGoMsg('');
    try {
      const res = await fetch('/api/challenge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'go', code: race.match }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'failed');
      takeStart(d);
      trackEvent('puzzle_race_together', { room: race.room });
    } catch (e) { setGoMsg('Could not start the countdown. Check your connection and try again.'); } finally { setGoBusy(false); }
  };

  // ---- zoom: + / − around the middle, and "show all" frames every piece ----
  const zoomBy = (k) => {
    const g = game.current;
    if (!g || !g.view) return;
    const v = g.view;
    const z = Math.max(0.35, Math.min(3, v.z * k));
    const cx = (g.W / 2 - v.ox) / v.z; const cy = (g.H / 2 - v.oy) / v.z;
    g.view = { z, ox: g.W / 2 - cx * z, oy: g.H / 2 - cy * z };
    draw();
  };
  const showAll = () => {
    const g = game.current;
    if (!g || !g.groups.length) return;
    const cw = g.S / g.cols; const ch = g.SH / g.rows;
    let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
    g.groups.forEach((grp) => {
      x0 = Math.min(x0, grp.x + grp.minC * cw); x1 = Math.max(x1, grp.x + (grp.maxC + 1) * cw);
      y0 = Math.min(y0, grp.y + grp.minR * ch); y1 = Math.max(y1, grp.y + (grp.maxR + 1) * ch);
    });
    const pad = 14;
    const z = Math.max(0.35, Math.min(1, (g.W - pad * 2) / (x1 - x0), (g.H - pad * 2) / (y1 - y0)));
    g.view = { z, ox: g.W / 2 - ((x0 + x1) / 2) * z, oy: g.H / 2 - ((y0 + y1) / 2) * z };
    draw();
  };

  // While a round is on, the page itself does not scroll: only the table moves,
  // so the clock and buttons stay put.
  useEffect(() => {
    if (phase !== 'play') return undefined;
    window.scrollTo(0, 0);
    document.documentElement.classList.add('jp-lock');
    // iPhone Safari can still drag the page under overflow:hidden; stop that,
    // except inside the confirm box.
    const stop = (e) => { if (!e.target.closest || !e.target.closest('.jp-confirm')) e.preventDefault(); };
    document.addEventListener('touchmove', stop, { passive: false });
    return () => {
      document.documentElement.classList.remove('jp-lock');
      document.removeEventListener('touchmove', stop);
    };
  }, [phase]);

  // ---- pointer handling --------------------------------------------------
  const pointFrom = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const v = (game.current && game.current.view) || { z: 1, ox: 0, oy: 0 };
    return { x: (e.clientX - r.left - v.ox) / v.z, y: (e.clientY - r.top - v.oy) / v.z };
  };

  const hit = (g, x, y) => {
    if (!scratch.current) scratch.current = document.createElement('canvas').getContext('2d');
    const sc = scratch.current;
    for (let i = g.groups.length - 1; i >= 0; i--) {
      const grp = g.groups[i];
      for (const p of grp.pieces) {
        if (sc.isPointInPath(g.paths[p.id], x - grp.x, y - grp.y)) return { grp, i };
      }
    }
    // a near miss on a small screen still picks the closest piece under the finger
    const cw = g.S / g.cols; const ch = g.SH / g.rows;
    for (let i = g.groups.length - 1; i >= 0; i--) {
      const grp = g.groups[i];
      for (const p of grp.pieces) {
        const px = grp.x + (p.c + 0.5) * cw; const py = grp.y + (p.r + 0.5) * ch;
        if (Math.abs(x - px) < cw * 0.6 && Math.abs(y - py) < ch * 0.6) return { grp, i };
      }
    }
    return null;
  };

  const onPointerDown = (e) => {
    const g = game.current;
    if (!g || g.won || g.peek || g.state !== 'playing' || phase !== 'play') return;
    e.preventDefault();
    const { x, y } = pointFrom(e);
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch (err) { /* synthetic events */ }
    const found = hit(g, x, y);
    if (found) {
      g.groups.splice(found.i, 1);
      g.groups.push(found.grp);
      g.drag = { grp: found.grp, dx: x - found.grp.x, dy: y - found.grp.y, id: e.pointerId };
    } else {
      // empty table: slide everything, handy when pieces drift off an edge
      g.drag = { pan: true, lx: x, ly: y, id: e.pointerId };
    }
    draw();
  };

  const onPointerMove = (e) => {
    const g = game.current;
    if (!g || !g.drag || g.drag.id !== e.pointerId) return;
    const { x, y } = pointFrom(e);
    if (g.drag.pan) {
      const dx = x - g.drag.lx; const dy = y - g.drag.ly;
      g.groups.forEach((grp) => { grp.x += dx; grp.y += dy; });
      g.drag.lx = x; g.drag.ly = y;
    } else {
      const grp = g.drag.grp;
      grp.x = x - g.drag.dx;
      grp.y = y - g.drag.dy;
    }
    draw();
  };

  const onPointerUp = (e) => {
    const g = game.current;
    if (!g || !g.drag || g.drag.id !== e.pointerId) return;
    const d = g.drag;
    g.drag = null;
    if (!d.pan) {
      // keep at least part of the group reachable
      const grp = d.grp;
      const cw = g.S / g.cols; const ch = g.SH / g.rows;
      const left = grp.x + grp.minC * cw; const right = grp.x + (grp.maxC + 1) * cw;
      const topY = grp.y + grp.minR * ch; const bottomY = grp.y + (grp.maxR + 1) * ch;
      const before = g.groups.length;
      // Join first: a piece dropped in its right place near an edge must not
      // be nudged off it (that made the last row of a low group impossible).
      // Only a piece that joined nothing is pulled back into reach.
      if (!tryMerge(g, grp)) {
        if (right < cw * 0.6) grp.x += cw * 0.6 - right;
        if (left > g.W - cw * 0.6) grp.x -= left - (g.W - cw * 0.6);
        if (bottomY < ch * 0.6) grp.y += ch * 0.6 - bottomY;
        if (topY > g.H - ch * 0.6) grp.y -= topY - (g.H - ch * 0.6);
      }
      setJoined(g.pieces.length - g.groups.length);
      saveSession();
      if (g.groups.length === 1) { finish(g); return; }
      if (g.groups.length < before) reportRace(g, false);
    }
    draw();
  };

  // Leaving or restarting a round in progress asks first.
  const inProgress = () => {
    const g = game.current;
    return g && !g.won && g.state === 'playing';
  };
  // Friends see it on the track when you restart or walk out of the race.
  const tellRace = (status) => {
    const r = raceRef.current;
    if (!r) return;
    submitChallengeScore({ code: r.match, name: r.name, score: 0, time: 0, final: false, status }).catch(() => {});
  };
  const leave = () => {
    const go = () => {
      if (inProgress()) tellRace('left');
      clearSession(); setConfirmBox(null);
      if (inGame) { leavingRef.current = true; navigate(-1); } else { game.current = null; setPhase('pick'); }
    };
    if (inProgress()) setConfirmBox({ text: 'Leave this puzzle? Your time and pieces will be lost.', yes: 'Leave', action: go });
    else go();
  };
  const restart = () => {
    const go = () => { setConfirmBox(null); tellRace('restarted'); start(slug, levelIdx, null, raceRef.current ? raceRef.current.seed : undefined); };
    if (inProgress()) setConfirmBox({ text: 'Start this puzzle again from the beginning?', yes: 'Restart', action: go });
    else go();
  };

  // Back (phone or header) while a round is open: ask if it is running,
  // otherwise just return to the picker.
  useEffect(() => {
    if (inGame || (phase !== 'play' && phase !== 'done' && phase !== 'loading')) return;
    if (inProgress() && !leavingRef.current) {
      saveSession();
      navigate('/puzzle', { state: { jpPlay: true } });
      setConfirmBox({
        text: 'Leave this puzzle? Your time and pieces will be lost.',
        yes: 'Leave',
        action: () => { clearSession(); setConfirmBox(null); leavingRef.current = true; navigate(-1); },
      });
      return;
    }
    leavingRef.current = false;
    clearSession();
    game.current = null;
    setConfirmBox(null);
    setPhase('pick');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inGame]);

  const togglePeek = () => {
    const g = game.current;
    if (!g || g.won) return;
    g.peek = !g.peek;
    setPeek(g.peek);
    draw();
  };

  const randomCharacter = () => {
    const others = CHARACTERS.filter(([s]) => s !== slug);
    return others[Math.floor(Math.random() * others.length)][0];
  };

  // ---- render ------------------------------------------------------------
  const total = level.pieces;
  const joins = total - 1;
  return (
    <div className="jp-page">
      <Helmet>
        <title>One Piece Jigsaw Puzzle — Beat the Clock</title>
        <meta name="description" content="Put Luffy, Zoro, Shanks and more back together. Pick 12, 30 or 64 pieces and see how fast you can solve it." />
      </Helmet>

      {phase === 'pick' || phase === 'loading' ? (
        <div className="jp-pick">
          <span className="not-on-board-note">Not yet in the leaderboard</span>
          <h1 className="jp-title">One Piece Jigsaw</h1>
          <p className="jp-sub">Pick a character and a size. You see the picture first, then press Start and it breaks apart.</p>
          <div className="jp-mode" role="tablist" aria-label="How to play">
            <button type="button" role="tab" aria-selected={mode === 'solo'} className={`jp-mode-btn ${mode === 'solo' ? 'is-on' : ''}`} onClick={goSolo}>
              <span className="jp-mode-icon">🧩</span> Solo
            </button>
            <button type="button" role="tab" aria-selected={mode === 'race'} className={`jp-mode-btn ${mode === 'race' ? 'is-on' : ''}`} onClick={goRace}>
              <span className="jp-mode-icon">🏁</span> Race friends
            </button>
          </div>

          {mode === 'race' && (
            <div className="jp-race-box">
              {!roomForRace ? (
                <>
                  <p className="jp-race-rule">Make a room, send the link, everyone gets the same pieces. Fastest time wins.</p>
                  <input className="jp-race-input" maxLength={18} value={raceName} placeholder="Your name first, e.g. Luffy" aria-label="Your name"
                    autoComplete="nickname" onChange={(e) => { setRaceName(e.target.value); setRaceMsg(''); }} />
                  <button type="button" className="jp-btn jp-btn-main jp-race-go" onClick={makeRaceRoom} disabled={raceBusy || raceName.trim().length < 2}>
                    {raceBusy ? <><span className="jp-spin" aria-hidden="true" /> Making the room…</> : 'Make a race room'}
                  </button>
                </>
              ) : (
                <>
                  <div className="jp-race-top">
                    <div className="jp-race-id">
                      <span className="jp-race-kicker">Room</span>
                      <span className="jp-race-code">{roomForRace}</span>
                    </div>
                    <button type="button" className="jp-share" onClick={shareRace}>{raceCopied ? '✓ Copied' : 'Share link'}</button>
                  </div>
                  <p className="jp-race-rule">Same pieces for everyone · start when you like · fastest time wins</p>
                  {roomMembers.length > 0 && (
                    <div className="jp-race-in">
                      {roomMembers.map((m) => <span key={m} className={`jp-race-member ${m.toLowerCase() === raceName.trim().toLowerCase() ? 'is-me' : ''}`}>{m}</span>)}
                    </div>
                  )}
                  {raceName.trim() && !editName && roomMembers.some((m) => m.toLowerCase() === raceName.trim().toLowerCase()) ? (
                    <p className="jp-race-you">You: <b>{raceName.trim()}</b> · <button type="button" className="jp-race-link" onClick={() => setEditName(true)}>change</button></p>
                  ) : (
                    <input id="jp-race-name" className="jp-race-input" maxLength={18} value={raceName} placeholder="Your name, e.g. Luffy" aria-label="Your name"
                      autoComplete="nickname" onChange={(e) => { setRaceName(e.target.value); setRaceMsg(''); }} />
                  )}
                  {raceMsg && <p className="jp-race-msg">{raceMsg}</p>}
                  <button type="button" className="jp-btn jp-btn-main jp-race-go" disabled={phase === 'loading'} onClick={startRace}>
                    {phase === 'loading' ? 'Loading…' : `Start race · ${NAME[slug]} · ${total}`}
                  </button>
                  <button type="button" className="jp-race-link jp-race-new" onClick={makeRaceRoom} disabled={raceBusy}>{raceBusy ? 'Making…' : '+ New room'}</button>
                </>
              )}
              {raceMsg && !roomForRace && <p className="jp-race-msg">{raceMsg}</p>}
              <RaceRules />
            </div>
          )}

          <div className="jp-label">Pieces</div>
          <div className="jp-levels">
            {LEVELS.map((lv, i) => {
              const b = best[`${slug}|${lv.pieces}`];
              return (
                <button key={lv.pieces} type="button" className={`jp-level ${i === levelIdx ? 'is-on' : ''}`} onClick={() => setLevelIdx(i)} aria-pressed={i === levelIdx}>
                  <strong>{lv.pieces}</strong>
                  <small>{b ? `Best ${formatTime(b)}` : 'pieces'}</small>
                </button>
              );
            })}
          </div>

          <div className="jp-label">Character</div>
          <div className="jp-chars">
            {CHARACTERS.map(([s, n]) => (
              <button key={s} type="button" className={`jp-char ${s === slug ? 'is-on' : ''}`} onClick={() => setSlug(s)} aria-pressed={s === slug}>
                <img src={thumbOf(s)} alt="" loading="lazy" />
                <span>{n}</span>
              </button>
            ))}
          </div>

          {history.length > 0 && (
            <>
              <div className="jp-label">Your puzzles</div>
              <div className="jp-history">
                {history.slice(0, 12).map((h) => (
                  <button
                    key={h.at}
                    type="button"
                    className="jp-hist"
                    onClick={() => { setSlug(h.slug); setLevelIdx(Math.max(0, LEVELS.findIndex((l) => l.pieces === h.pieces))); }}
                    title={`Play ${NAME[h.slug] || h.slug} again`}
                  >
                    <img src={thumbOf(h.slug)} alt="" loading="lazy" />
                    <span className="jp-hist-body">
                      <strong>{NAME[h.slug] || h.slug}</strong>
                      <span className="jp-hist-time">{formatTime(h.ms)}{best[`${h.slug}|${h.pieces}`] === h.ms ? ' ★' : ''}</span>
                      <small>{h.pieces} pieces · {whenLabel(h.at)}</small>
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}


          {mode === 'solo' && (
            <div className="jp-actions jp-actions-sticky">
              <button type="button" className="jp-btn jp-btn-main" disabled={phase === 'loading'} onClick={() => start(slug, levelIdx)}>
                {phase === 'loading' ? 'Loading…' : `Start · ${NAME[slug]} · ${total}`}
              </button>
              <button type="button" className="jp-btn" disabled={phase === 'loading'} onClick={() => { const s = randomCharacter(); setSlug(s); start(s, levelIdx); }}>
                Random
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="jp-play">
          <div className="jp-bar">
            <span className="jp-clock">{formatTime(elapsed)}</span>
            <span className="jp-count">{joined}/{joins} joined</span>
            <span className="jp-bar-gap" />
            {phase === 'play' && !ready && (
              <button type="button" className={`jp-chip ${peek ? 'is-on' : ''}`} onClick={togglePeek}>
                {peek ? 'Back' : 'Peek'}
              </button>
            )}
            {phase === 'play' && !ready && (
              <button type="button" className="jp-chip" onClick={restart}>Restart</button>
            )}
            <button type="button" className="jp-chip" onClick={leave}>Change</button>
          </div>
          {race && (() => {
            // The track: one lane per racer, a ship moving along it with every
            // join. Your own lane follows this round live; others show their
            // latest report. Finished first (by time), then furthest along.
            const me = race.name.toLowerCase();
            // Others: their current round (latest report in the last hour), not
            // their best — a finished best from an old round is not this race.
            // …and only reports from after this round began (2 min grace for
            // friends waiting on the picture): a finish from an earlier round
            // is not in this race.
            const fresh = (p) => p.cur && new Date(p.cur.at).getTime() > roundAt.current - 120e3;
            const lanes = racePlayers.filter((p) => p.name.toLowerCase() === me || fresh(p)).map((p) => {
              const mine = p.name.toLowerCase() === me;
              const cur = p.cur || { score: p.score, time: p.totalTime };
              const done = mine ? !!result : cur.score >= joins;
              const score = mine ? (result ? joins : joined) : cur.score;
              // "restarted" is news for a moment, then they are simply ready again
              const status = mine ? null : (cur.status === 'restarted' && Date.now() - new Date(cur.at).getTime() > 15e3 ? 'ready' : cur.status);
              return { name: p.name, avatar: p.avatar, mine, done, score, status, time: mine && result ? Math.floor(result.ms / 1000) : cur.time };
            });
            if (!lanes.some((l) => l.mine)) lanes.push({ name: race.name, mine: true, done: !!result, score: result ? joins : joined, time: 0 });
            lanes.sort((a, b) => ((a.status === 'left') - (b.status === 'left')) || (b.done - a.done) || (a.done ? a.time - b.time : b.score - a.score));
            return (
              <div className="jp-track" aria-label="Race">
                <div className="jp-track-head">
                  <button type="button" className="jp-race-tag" onClick={shareRace} title="Share the race link">🏁 {race.room} · {raceCopied ? '✓ Copied' : 'Share'}</button>
                  {/* One line, always the same height: news replaces "Racing as" for a moment */}
                  {lateMsg || joinNote
                    ? <span className="jp-track-me jp-track-news">{lateMsg || joinNote}</span>
                    : lanes.length < 2
                      ? <span className="jp-track-me">Alone so far · tap Share</span>
                      : <span className="jp-track-me">Racing as <b>{race.name}</b></span>}
                </div>
                <div className="jp-lanes">
                  {lanes.map((l) => {
                    const pct = Math.min(100, Math.round((l.score / joins) * 100));
                    return (
                      <div key={l.name} className={`jp-lane ${l.mine ? 'is-me' : ''} ${l.done ? 'is-done' : ''} ${l.status === 'left' ? 'is-left' : ''}`}>
                        <span className="jp-lane-who" title={l.name}>
                          {l.avatar ? <Avatar value={l.avatar} className="jp-lane-ava" alt="" /> : <span className="jp-lane-ava jp-lane-ava-none" aria-hidden="true">{l.name.charAt(0).toUpperCase()}</span>}
                          <span className="jp-lane-name">{l.name}</span>
                        </span>
                        <span className="jp-lane-road">
                          <span className="jp-lane-fill" style={{ width: `${pct}%` }} />
                          <span className="jp-lane-ship" style={{ left: `${pct}%` }}>{l.done ? '🏁' : '⛵'}</span>
                        </span>
                        <span className="jp-lane-val">{l.status === 'left' ? 'left' : l.status === 'restarted' ? 'restarted' : l.done ? formatTime(l.time * 1000) : l.score === 0 ? 'ready' : `${l.score}/${joins}`}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          <div className="jp-stage" ref={wrapRef}>
            <canvas
              ref={canvasRef}
              className="jp-canvas"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            />
            {confirmBox && (
              <div className="jp-confirm" role="dialog" aria-modal="true">
                <div className="jp-confirm-card">
                  <p>{confirmBox.text}</p>
                  <div className="jp-actions">
                    <button type="button" className="jp-btn" onClick={() => setConfirmBox(null)}>Keep playing</button>
                    <button type="button" className="jp-btn jp-btn-main" onClick={confirmBox.action}>{confirmBox.yes}</button>
                  </div>
                </div>
              </div>
            )}
            {phase === 'done' && result && (
              <div className="jp-done">
                <div className="jp-done-card">
                  <div className="jp-done-name">{NAME[slug]} · {total} pieces</div>
                  <div className="jp-done-time">{formatTime(result.ms)}</div>
                  {!race && (
                    <div className="jp-done-best">
                      {result.record ? 'New best time' : `Best ${formatTime(best[`${slug}|${total}`])}`}
                    </div>
                  )}
                  {race && (() => {
                    // In a race the card says one thing: where you came this round.
                    const me = race.name.toLowerCase();
                    const myTime = Math.floor(result.ms / 1000);
                    const round = racePlayers
                      .filter((p) => p.name.toLowerCase() !== me && p.cur && p.cur.score >= joins && new Date(p.cur.at).getTime() > roundAt.current - 120e3)
                      .map((p) => p.cur.time).concat([myTime]).sort((a, b) => a - b);
                    const place = round.indexOf(myTime) + 1;
                    const medal = ['🥇', '🥈', '🥉'][place - 1] || `#${place}`;
                    const others = lanesLeft();
                    return (
                      <div className="jp-race-place">
                        <span className="jp-place-medal">{medal}</span> {place === 1 ? '1st' : place === 2 ? '2nd' : place === 3 ? '3rd' : `${place}th`} of {round.length}
                        {others > 0 && <span className="jp-place-wait"> · {others} still racing</span>}
                      </div>
                    );
                  })()}
                  <div className="jp-actions">
                    <button type="button" className="jp-btn jp-btn-main" onClick={() => { trackEvent('puzzle_play_again', { character: slug }); start(slug, levelIdx, null, race ? race.seed : undefined); }}>{race ? 'Race again' : 'Play again'}</button>
                    {!race && <button type="button" className="jp-btn" onClick={() => { trackEvent('puzzle_next_character', { from: slug }); const s = randomCharacter(); setSlug(s); start(s, levelIdx); }}>Next character</button>}
                    <button type="button" className="jp-btn" onClick={leave}>Change</button>
                  </div>
                  {!race && <GuestNudge compact />}
                </div>
              </div>
            )}
          </div>
          {/* The row under the board: Start buttons before the round, zoom after.
              Nothing is ever drawn on top of the picture or a piece. */}
          <div className="jp-zoom-row" aria-label={ready ? 'Start' : 'Zoom'}>
            {phase === 'play' && ready ? (
              goAt ? (
                <div className="jp-countdown" aria-live="assertive">
                  Everyone starts in <b>{Math.max(1, Math.ceil(goLeft / 1000))}</b>
                </div>
              ) : (
                <>
                  <button type="button" className="jp-btn jp-btn-main jp-row-go" onClick={breakApart}>Start game</button>
                  {race && (
                    <button type="button" className="jp-btn jp-together" onClick={startTogetherNow} disabled={goBusy}
                      title="Everyone on the picture screen starts with you. Anyone already playing keeps their own clock.">
                      {goBusy ? <><span className="jp-spin" aria-hidden="true" /> Starting…</> : '⏱ Start together'}
                    </button>
                  )}
                </>
              )
            ) : (
              <>
                <button type="button" onClick={() => zoomBy(1 / 1.3)} aria-label="Zoom out" disabled={!!result || peek}>−</button>
                <button type="button" onClick={showAll} aria-label="Show every piece" disabled={!!result || peek}>⤢ Show all</button>
                <button type="button" onClick={() => zoomBy(1.3)} aria-label="Zoom in" disabled={!!result || peek}>+</button>
              </>
            )}
          </div>
          {phase === 'play' && ready && goMsg && <p className="jp-together-note">{goMsg}</p>}
        </div>
      )}
    </div>
  );
}
